import { useCart } from '@shared/cart-react';
import { useProducts } from '@shared/react-product';
import { graphql, useStaticQuery } from 'gatsby';
import isEmpty from 'lodash/isEmpty';
import queryString from 'query-string';
import {
  ComponentProps,
  PropsWithChildren,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { BaseContext, BaseContextType } from '../../context/base';
import { IsNavigatingProvider } from '../../context/isNavigating';
import '../../styles/global.css';
import '../../styles/tailwind.css';
import SlideCart from '../cart/cart';
import AccessibilityStatement from '../header/accessibilityStatement';
import AnnouncementBanner from '../header/announcementBanner';
import CustomerSalesBanner from '../header/customerSalesBanner';
import PromoBannerSubscribe from '../header/promoBannerSubscribe';

import { Drawer } from '@shared/cove-ui';
import { Layout as NewLayout, SmoothMount } from '@shared/react-ui-core';
import { AffirmProvider } from '@website/context/affirm';
import { PartnerObject } from '@website/data/partners';
import useMonetateLoaded from '@website/hooks/useMonetateLoaded';
import useTrackKlaviyoFormEvent from '@website/hooks/useTrackKlaviyoFormEvent';
import { localStore } from '../../libs/storage';
import { signup } from '../../services/marketingSvc';
import EightyPercentDiscountBanner from '../header/eightyPercentDiscount';

const EXCLUDED_SKUS = ['CP-YS-23i-1', 'window-sticker-sm'];

export interface LayoutProps extends PropsWithChildren {
  hideCart?: boolean;
  hidePromo?: boolean;
  hideNav?: boolean;
  hideFooter?: boolean;
  logoLink?: React.ReactNode | string;
  promoBottom?: boolean;
  isMinimal?: boolean;
  minimalFooter?: boolean;
  clickToCall?: boolean | { hideChat?: boolean };
  callNumber?: string;
  isRetail?: boolean;
  navLeft?: JSX.Element;
  navRight?: JSX.Element;
  backgroundColor?: string;
  variant?: ComponentProps<typeof NewLayout>['variant'];
  partnerPage?: boolean;
  AdditionalBanners?: ReactNode;
  bannerClassName?: string;
}

const Layout: React.FC<LayoutProps> = ({
  children,
  hideCart,
  hidePromo,
  hideNav,
  hideFooter,
  logoLink,
  promoBottom,
  isMinimal,
  minimalFooter,
  navLeft,
  navRight,
  clickToCall,
  callNumber,
  isRetail,
  backgroundColor,
  variant,
  partnerPage = false,
  AdditionalBanners,
  bannerClassName,
  ...props
}) => {
  const data = useStaticQuery(graphql`
    query LayoutQuery {
      datoCmsSiteMetaInfo {
        promoBannerTitle
        promoBannerTitleTextColor {
          hex
        }
        promoBannerBoldText
        promoBannerBoldTextColor {
          hex
        }
        promoBannerText
        promoBannerTextColor {
          hex
        }
        promoBannerSaleName
        promoSaleEndDate
        universalDiscount
        panelDiscount
      }
      allDatoCmsAffiliate {
        nodes {
          partnerPromoCode {
            couponCode
          }
        }
      }
    }
  `);
  const { datoCmsSiteMetaInfo, allDatoCmsAffiliate } = data;
  const cart = useCart();
  const products = useProducts();
  const {
    productShowCart,
    setProductShowCart,
    dismissSideCart,
    sideCartDismissed,
  } = useContext<BaseContextType>(BaseContext);
  const showCart = () => setProductShowCart(!productShowCart);
  useTrackKlaviyoFormEvent();

  const getCartQuantity = () => {
    if (isEmpty(cart)) {
      return 0;
    }
    const { cartItemIds = [] } = cart;
    const count = cartItemIds.reduce((sum = 0, sku) => {
      const product = products[sku];
      const cartProduct = cart?.cartProducts?.[sku];
      if (
        !EXCLUDED_SKUS.includes(sku) &&
        cartProduct?.qty &&
        (product?.productType === 'equipment' ||
          product?.productType === 'package')
      ) {
        sum += cartProduct?.qty;
      }
      return sum;
    }, 0);

    return count;
  };

  const cartCount = getCartQuantity() || 0;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const {
      promoBannerTitle,
      promoBannerTitleTextColor,
      promoBannerBoldText,
      promoBannerBoldTextColor,
      promoBannerText,
      promoBannerTextColor,
      universalDiscount,
      panelDiscount,
    } = datoCmsSiteMetaInfo;

    try {
      // @ts-ignore
      window.dyno?.service?.track?.('banner', {
        properties: {
          title: promoBannerTitle,
          titleColor: promoBannerTitleTextColor.hex,
          boldText: promoBannerBoldText,
          boldTextColor: promoBannerBoldTextColor.hex,
          text: promoBannerText,
          textColor: promoBannerTextColor.hex,
          universalDiscount: universalDiscount,
          panelDiscount: panelDiscount,
        },
      });
    } catch (error) {
      console.error('Error dyno.service.track banner', error);
    }

    let googleBadge = document.getElementById('___ratingbadge_0');
    let googleIframe = googleBadge
      ? googleBadge.querySelector('iframe')
      : undefined;

    if (googleIframe) {
      googleIframe.title = 'Google Customer Reviews';
    }
  });

  const params =
    typeof window !== `undefined`
      ? queryString.parse(window.location.search)
      : null;
  const utm_source = params?.utm_source;
  const promoCode = params?.promoCode;
  const showSalesBanner = utm_source === 'rokt';

  const [showRorBanner, setShowRorBanner] = useState(false);
  const monetateLoaded = useMonetateLoaded();

  useEffect(() => {
    if (cart.couponCodes) {
      setShowRorBanner(
        cart.couponCodes.includes('WELCOMEBACK') || promoCode === 'WELCOMEBACK',
      );
    }
  }, [cart.couponCodes, promoCode]);

  useEffect(() => {
    const _ActiveOnSiteEvent = {
      domain_identifier: window.location.hostname,
      site: window.location.href,
      referrer: document.referrer,
    };
    // Call to Retention's Active on Site event
    window.geq?.event?.('Active on Site Reclaim', _ActiveOnSiteEvent);

    const excludeRetentionPages = ['customer-info', 'payment'];
    if (
      typeof window !== 'undefined' &&
      !excludeRetentionPages.some((page) => window.location.href.includes(page))
    ) {
      window.geq?.page?.();
    }
  }, []);

  const handleSubscribe = async (email: string) => {
    const partnerId = localStore.getItem('partnerId');
    const reqData = {
      email,
      cartId: cart.cartId,
      partnerId: null,
    };
    if (partnerId) reqData.partnerId = partnerId;
    return await signup(reqData)
      .then(() => {
        return { status: 'success' };
      })
      .catch((err) => {
        if (
          err?.response?.status === 400 &&
          err?.response?.data?.source === 'Invalid Email'
        ) {
          return { status: 'unverifiedEmail' };
        }
        console.error('adding email sub failed :(', err);
        return { status: 'error' };
      });
  };

  const getBanner = () => {
    if (showRorBanner) {
      return <EightyPercentDiscountBanner />;
    }

    if (showSalesBanner) {
      return <CustomerSalesBanner active={showSalesBanner} />;
    }

    const partners = allDatoCmsAffiliate.nodes as Partial<PartnerObject>[];
    const partnersPromoCodes = partners.map(
      (partner) => partner.partnerPromoCode?.couponCode,
    );
    const hasPartnerPromo = (cart?.couponCodes || []).some((couponCodes) =>
      partnersPromoCodes.includes(couponCodes),
    );

    // If the current page is a partner page (/perkspot, /benefithub, etc.), use the hidePromo prop,
    // otherwise, hide the banner if the customer has a partner promocode applied OR if the hidePromo
    // prop is true.
    const promoHidden = partnerPage ? hidePromo : hasPartnerPromo || hidePromo;

    return (
      <PromoBannerSubscribe
        active={!promoHidden}
        className={bannerClassName}
        data={data}
      />
    );
  };

  return (
    <IsNavigatingProvider>
      <Drawer
        open={productShowCart}
        onOpenChange={(state) => {
          setProductShowCart(state);
          if (!sideCartDismissed) {
            dismissSideCart();
          }
        }}
      >
        <div
          style={{ backgroundColor: `${backgroundColor ?? '#fff'}` }}
          className="relative mx-auto my-0 w-full"
        >
          <AnnouncementBanner />
          {/* We wait for monetate to be loaded because Acadia runs tests on the banner and loading the banner before Acadia can run their test code causes tons of flickering. (This also makes our CLS score worse, but it's a tradeoff we have to make if we want to test our banner ¯\_(ツ)_/¯) */}
          <SmoothMount show={monetateLoaded}>{getBanner()}</SmoothMount>
          {AdditionalBanners}
          <NewLayout
            onEmailSubscribe={handleSubscribe}
            isMinimal={isMinimal || isRetail}
            logoLink={logoLink}
            minimalFooter={minimalFooter}
            navLeft={navLeft}
            navRight={navRight}
            variant={variant ?? 'base'}
            {...{
              cartCount,
              showCart,
              hideNav,
              hideFooter,
              clickToCall,
              callNumber,
            }}
          >
            <AffirmProvider>
              <AccessibilityStatement />
              {!hideCart && <SlideCart />}
              <div>{children}</div>
            </AffirmProvider>
          </NewLayout>
        </div>
      </Drawer>
    </IsNavigatingProvider>
  );
};

export default Layout;
